import React, { FunctionComponent, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { DateUtils } from 'record-it-utils/utils/DateUtils/DateUtils';
import { DurationUtils } from 'record-it-utils/utils/DurationUtils/DurationUtils';
import { WorkTimeEntry } from '../../../classes/entities/WorkTimeEntry/workTimeEntryTypes';
import { WorkTimeEntryUtils } from '../../../classes/entities/WorkTimeEntry/WorkTimeEntryUtils';
import { useTimeTrackingActivityPathName } from '../../../hooks/entity/timeTrackingActivity/useTimeTrackingActivityPathName';
import { useEntityByIdOrOriginalId } from '../../../hooks/entity/useEntityByIdOrOriginalId';
import styles from './TodaysWorkTimeInfoDetailEntry.module.css';

export type TodaysWorkTimeInfoDetailEntryProps = PropsWithoutChildren<
  ComponentProps<'div'>
> & {
  workTimeEntry: WorkTimeEntry;
};

export const TodaysWorkTimeInfoDetailEntry: FunctionComponent<TodaysWorkTimeInfoDetailEntryProps> =
  React.memo((props) => {
    const { workTimeEntry, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });

    const { t } = useTranslation();

    const constructionSite = useEntityByIdOrOriginalId(
      EntityName.ConstructionSite,
      workTimeEntry.constructionSiteId
    );
    const activityPathName = useTimeTrackingActivityPathName(
      workTimeEntry.timeTrackingActivityId
    );

    const descriptionParts: Array<string> = [];

    if (constructionSite) {
      descriptionParts.push(constructionSite.name);
    }

    if (activityPathName) {
      descriptionParts.push(activityPathName);
    }

    const description = descriptionParts.join('; ');

    return (
      <div {...remainingProps}>
        <span className={styles.durationInfo}>
          {DateUtils.withCurrentTimezone().formatDateRange(
            workTimeEntry.startDate,
            workTimeEntry.endDate
          )}{' '}
          (
          {DurationUtils.formatDurationToHourMinuteString(
            WorkTimeEntryUtils.getWorkTimeEntryBreakDuration(workTimeEntry)
          )}{' '}
          {t('components.workTime.TodaysWorkTimeInfoDetailEntry.break')})
        </span>
        {description ? (
          <span>
            {'; '}
            {description}
          </span>
        ) : null}
      </div>
    );
  });
